import React, { useCallback, useEffect, useRef, useState } from "react";
import { MAX_AVATAR_PHOTO_CHARS } from "../../../lib/avatars/index.js";

const VIEW_SIZE = 260;
const OUTPUT_SIZE = 192;
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const QUALITY_STEPS = [0.82, 0.7, 0.58, 0.45, 0.35];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const readFile = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("No se pudo leer la imagen"));
        reader.readAsDataURL(file);
    });

const loadImage = (src) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error("La imagen no es válida"));
        image.src = src;
    });

const compress = (canvas) => {
    for (const quality of QUALITY_STEPS) {
        const dataUrl = canvas.toDataURL("image/jpeg", quality);
        if (dataUrl.length <= MAX_AVATAR_PHOTO_CHARS) return dataUrl;
    }
    return null;
};

const AvatarCropModal = ({ open, file, onCancel, onSave, saving = false }) => {
    const [image, setImage] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [error, setError] = useState("");

    const dragRef = useRef(null);
    const baseScaleRef = useRef(1);
    const viewportRef = useRef(null);

    useEffect(() => {
        if (!open || !file) return;

        let cancelled = false;
        setImage(null);
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setError("");

        (async () => {
            try {
                const loaded = await loadImage(await readFile(file));
                if (cancelled) return;

                baseScaleRef.current = Math.max(
                    VIEW_SIZE / loaded.naturalWidth,
                    VIEW_SIZE / loaded.naturalHeight,
                );
                setImage(loaded);
            } catch (e) {
                if (!cancelled) setError(e.message);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, file]);

    const clampOffset = useCallback(
        (next, nextZoom) => {
            if (!image) return { x: 0, y: 0 };

            const scale = baseScaleRef.current * nextZoom;
            const maxX = Math.max(
                0,
                (image.naturalWidth * scale - VIEW_SIZE) / 2,
            );
            const maxY = Math.max(
                0,
                (image.naturalHeight * scale - VIEW_SIZE) / 2,
            );

            return {
                x: clamp(next.x, -maxX, maxX),
                y: clamp(next.y, -maxY, maxY),
            };
        },
        [image],
    );

    useEffect(() => {
        setOffset((current) => clampOffset(current, zoom));
    }, [zoom, clampOffset]);

    useEffect(() => {
        if (!open) return;

        const onMove = (event) => {
            if (!dragRef.current) return;
            setOffset(
                clampOffset(
                    {
                        x:
                            dragRef.current.originX +
                            event.clientX -
                            dragRef.current.startX,
                        y:
                            dragRef.current.originY +
                            event.clientY -
                            dragRef.current.startY,
                    },
                    zoom,
                ),
            );
        };
        const onUp = () => {
            dragRef.current = null;
        };

        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onUp);
        return () => {
            document.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseup", onUp);
        };
    }, [open, zoom, clampOffset]);

    useEffect(() => {
        if (!open) return;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [open]);

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!open || !image || !viewport) return;

        const onWheel = (event) => {
            event.preventDefault();

            const delta =
                event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
            if (!Number.isFinite(delta)) return;

            setZoom((current) =>
                clamp(current - delta * 0.002, MIN_ZOOM, MAX_ZOOM),
            );
        };

        viewport.addEventListener("wheel", onWheel, { passive: false });
        return () => viewport.removeEventListener("wheel", onWheel);
    }, [open, image]);

    if (!open) return null;

    const onMouseDown = (event) => {
        event.preventDefault();
        dragRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        };
    };

    const handleSave = () => {
        if (!image) return;

        const canvas = document.createElement("canvas");
        canvas.width = OUTPUT_SIZE;
        canvas.height = OUTPUT_SIZE;

        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

        const ratio = OUTPUT_SIZE / VIEW_SIZE;
        const scale = baseScaleRef.current * zoom * ratio;
        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;

        ctx.drawImage(
            image,
            OUTPUT_SIZE / 2 + offset.x * ratio - width / 2,
            OUTPUT_SIZE / 2 + offset.y * ratio - height / 2,
            width,
            height,
        );

        const dataUrl = compress(canvas);
        if (!dataUrl) {
            setError(
                "La foto sigue siendo demasiado grande. Prueba con una imagen más pequeña.",
            );
            return;
        }

        onSave(dataUrl);
    };

    const displayScale = baseScaleRef.current * zoom;

    return (
        <div
            className="alert-modal-overlay"
            onClick={saving ? undefined : onCancel}
        >
            <div
                className="avatar-crop-container"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="avatar-crop-title">Ajusta tu foto</div>
                <p className="avatar-crop-hint">
                    Arrastra la imagen y usa la rueda o el control para elegir
                    qué parte se verá en el círculo.
                </p>

                <div
                    className="avatar-crop-viewport"
                    ref={viewportRef}
                    style={{ width: VIEW_SIZE, height: VIEW_SIZE }}
                    onMouseDown={image ? onMouseDown : undefined}
                >
                    {image && (
                        <img
                            className="avatar-crop-image"
                            src={image.src}
                            alt=""
                            draggable={false}
                            style={{
                                width: image.naturalWidth * displayScale,
                                height: image.naturalHeight * displayScale,
                                transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                            }}
                        />
                    )}
                    <div className="avatar-crop-mask" />
                </div>

                <input
                    type="range"
                    className="avatar-crop-zoom"
                    min={MIN_ZOOM * 100}
                    max={MAX_ZOOM * 100}
                    value={Math.round(zoom * 100)}
                    onChange={(e) => setZoom(Number(e.target.value) / 100)}
                    disabled={!image}
                />

                {error && <p className="avatar-crop-error">{error}</p>}

                <div className="avatar-crop-actions">
                    <button
                        type="button"
                        className="avatar-crop-btn secondary"
                        onClick={onCancel}
                        disabled={saving}
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="avatar-crop-btn"
                        onClick={handleSave}
                        disabled={!image || saving}
                    >
                        {saving ? "Guardando…" : "Guardar avatar"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AvatarCropModal;
